// frontend/src/components/admin/WebTrafficCard.tsx
//
// Self-hosted web-traffic panel. Reads GET /admin/analytics/web (page_views
// aggregates) and renders totals, a by-day trend, top pages, and top referrers.
// Independent of the product-metrics overview in PlatformAnalyticsTab.

import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress, Alert,
  Button, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton,
  ToggleButtonGroup, Divider,
} from '@mui/material';
import { Refresh, Public } from '@mui/icons-material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { api } from '../../utils/api';

const TEAL = '#2D6E6A';

interface WebStats {
  success: boolean;
  rangeDays: number;
  totals: { views: number; sessions: number; known_users: number };
  topPages: { path: string; views: number }[];
  topReferrers: { referrer: string; views: number }[];
  byDay: { day: string; views: number; sessions: number }[];
}

const Tile: React.FC<{ label: string; value: number | string; sub?: string }> = ({ label, value, sub }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent sx={{ py: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

const WebTrafficCard: React.FC = () => {
  const [data, setData] = useState<WebStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  const load = async (rangeDays: number) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/admin/analytics/web?days=${rangeDays}`);
      const result = await response.json();
      if (response.ok && result.success) {
        setData(result);
      } else {
        throw new Error(result.error || 'Failed to load web traffic');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load web traffic');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  return (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Public sx={{ color: TEAL }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Web traffic</Typography>
            <Typography variant="caption" color="text.secondary">(self-hosted, cookieless; bots excluded)</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={days}
              onChange={(_e, v) => { if (v) setDays(v); }}
            >
              <ToggleButton value={7}>7d</ToggleButton>
              <ToggleButton value={30}>30d</ToggleButton>
              <ToggleButton value={90}>90d</ToggleButton>
            </ToggleButtonGroup>
            <Button startIcon={<Refresh />} onClick={() => load(days)} variant="outlined" size="small">Refresh</Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && !data ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={20} /><Typography variant="body2">Loading web traffic...</Typography>
          </Box>
        ) : data ? (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={4}><Tile label="Page views" value={data.totals.views} sub={`last ${data.rangeDays}d`} /></Grid>
              <Grid item xs={4}><Tile label="Unique sessions" value={data.totals.sessions} /></Grid>
              <Grid item xs={4}><Tile label="Signed-in visitors" value={data.totals.known_users} /></Grid>
            </Grid>

            {data.byDay.length > 0 && (
              <Box sx={{ width: '100%', height: 220, mb: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.byDay} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="wtViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={TEAL} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d: string) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="views" stroke={TEAL} strokeWidth={2} fill="url(#wtViews)" name="Views" />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Top pages</Typography>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Path</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Views</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {data.topPages.map((p) => (
                      <TableRow key={p.path}>
                        <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.path}</TableCell>
                        <TableCell align="right">{p.views}</TableCell>
                      </TableRow>
                    ))}
                    {data.topPages.length === 0 && (
                      <TableRow><TableCell colSpan={2} align="center"><Typography variant="caption" color="text.secondary">No data yet</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Top referrers</Typography>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Referrer</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Views</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {data.topReferrers.map((r) => (
                      <TableRow key={r.referrer}>
                        <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.referrer}</TableCell>
                        <TableCell align="right">{r.views}</TableCell>
                      </TableRow>
                    ))}
                    {data.topReferrers.length === 0 && (
                      <TableRow><TableCell colSpan={2} align="center"><Typography variant="caption" color="text.secondary">No referrers yet (direct traffic)</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default WebTrafficCard;
